import { getCheckSteps, hasCheckedIn } from 'core/domain/Stop'
import { type ShipmentTag } from 'core/shipment-tag/domain/ShipmentTag'

import {
  type BaseShipment,
  hasDriverAssigned,
  hasTractorNumberAssigned,
  hasLastLocation,
  ShipmentNextStep,
} from './BaseShipment'

export const enum AgnosticShipmentStatus {
  AccountingReview = 'accounting-review',
  Archived = 'archived',
  AtDelivery = 'at-delivery',
  AtPickup = 'at-pickup',
  Booked = 'booked',
  Canceled = 'canceled',
  Deleted = 'deleted',
  Delivered = 'delivered',
  EnRouteDelivery = 'en-route-delivery',
  EnRoutePickup = 'en-route-pickup',
}

export const AGNOSTIC_STATUS: Array<AgnosticShipmentStatus> = [
  AgnosticShipmentStatus.EnRoutePickup,
  AgnosticShipmentStatus.AtPickup,
  AgnosticShipmentStatus.EnRouteDelivery,
  AgnosticShipmentStatus.AtDelivery,
  AgnosticShipmentStatus.AccountingReview,
  AgnosticShipmentStatus.Booked,
  AgnosticShipmentStatus.Delivered,
  AgnosticShipmentStatus.Deleted,
  AgnosticShipmentStatus.Canceled,
  AgnosticShipmentStatus.Archived,
]

export const AGNOSTIC_HISTORY_STATUS: Array<AgnosticShipmentStatus> = [
  AgnosticShipmentStatus.Archived,
  AgnosticShipmentStatus.Canceled,
  AgnosticShipmentStatus.Deleted,
  AgnosticShipmentStatus.Delivered,
]

export const AGNOSTIC_ONGOING_STATUS: Array<AgnosticShipmentStatus> = [
  AgnosticShipmentStatus.AccountingReview,
  AgnosticShipmentStatus.AtDelivery,
  AgnosticShipmentStatus.AtPickup,
  AgnosticShipmentStatus.EnRouteDelivery,
  AgnosticShipmentStatus.EnRoutePickup,
]

export const AGNOSTIC_UPCOMING_STATUS: Array<AgnosticShipmentStatus> = [
  AgnosticShipmentStatus.Booked,
]

export type ShipmentIdentifier = 'bol' | 'container' | 'po' | 'reference' | 'so'

export const DEFAULT_SHIPMENT_IDENTIFIERS: Array<ShipmentIdentifier> = [
  'bol',
  'po',
  'so',
  'reference',
]

export type AgnosticShipment = BaseShipment & {
  status: AgnosticShipmentStatus
  equipmentLength?: string
  equipmentSubType?: string
  poNumbers?: string[]
  soNumbers?: string[]
  bolNumber: string | null
  tags?: Array<ShipmentTag>
}

export function canUploadDocuments({ status }: AgnosticShipment) {
  return status === AgnosticShipmentStatus.AccountingReview
}

export function hasPreCheckToComplete(shipment: AgnosticShipment) {
  const hasDriver = hasDriverAssigned(shipment)
  const hasLocation = hasLastLocation(shipment)
  const hasTractorNumber = hasTractorNumberAssigned(shipment)

  return !hasDriver || !hasLocation || !hasTractorNumber
}

export function getNextStep(shipment: AgnosticShipment) {
  /**
   * If we have some history status, we should
   * skip showing the next action button
   */
  if (shouldSkipNextStep(shipment)) return

  const canUploadDocs = canUploadDocuments(shipment)
  const hasPendingPreCheck = hasPreCheckToComplete(shipment)
  const hasChecked = hasCheckedIn(shipment.stops)
  const { checkIn, checkOut } = getCheckSteps(shipment.stops)

  /**
   * If some stop is already checked, we should skip the pre-pickup
   * actions and show the pickup next actions as priority
   */
  if (hasPendingPreCheck && hasChecked) {
    if (checkIn) return ShipmentNextStep.CheckIn
    if (checkOut) return ShipmentNextStep.CheckOut
    if (canUploadDocs) return ShipmentNextStep.UploadDocuments
  }

  /**
   * If the shipment is in accounting review we should
   * show the upload docs next action as priority
   */
  if (canUploadDocs) return ShipmentNextStep.UploadDocuments

  /**
   * Otherwise, we show the next actions in the order defined below
   */

  if (!hasDriverAssigned(shipment)) return ShipmentNextStep.AssignDriver
  if (!hasTractorNumberAssigned(shipment)) return ShipmentNextStep.AddTruckingInfo
  if (!hasLastLocation(shipment)) return ShipmentNextStep.AddTracking
  if (checkIn) return ShipmentNextStep.CheckIn
  if (checkOut) return ShipmentNextStep.CheckOut
}

export function shouldSkipNextStep({ status }: AgnosticShipment) {
  return AGNOSTIC_HISTORY_STATUS.includes(status)
}
